import {
  BookOpen,
  ChevronDown,
  GitCompare,
  Layers,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  PlusCircle,
  Radar,
  ShieldCheck,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Form, Link, NavLink } from 'react-router';
import { buttonVariants } from '~/components/ui/button';
import { cn } from '~/lib/utils';

type NavUser = {
  id: string;
  name: string;
  email: string;
  image: string | null;
  isAdmin: boolean;
} | null;

const PRIMARY_LINKS = [
  { to: '/compare', label: 'Compare', icon: GitCompare },
  { to: '/compose', label: 'Compose', icon: Layers },
  { to: '/reverse', label: 'Reverse', icon: Radar },
];

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
}

export default function Nav({ user }: { user: NavUser }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, []);

  return (
    <header className="border-b border-border bg-background sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold text-foreground"
          >
            <BookOpen className="size-5 text-primary" />
            <span>Specly</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {PRIMARY_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  cn(
                    buttonVariants({ variant: 'ghost', size: 'sm' }),
                    isActive && 'bg-accent text-accent-foreground',
                  )
                }
              >
                <link.icon data-icon="inline-start" />
                {link.label}
              </NavLink>
            ))}
            {user && (
              <NavLink
                to="/submit"
                className={({ isActive }) =>
                  cn(
                    buttonVariants({ variant: 'ghost', size: 'sm' }),
                    isActive && 'bg-accent text-accent-foreground',
                  )
                }
              >
                <PlusCircle data-icon="inline-start" />
                Submit
              </NavLink>
            )}
            {user ? (
              <div className="relative ml-2" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-muted transition-colors"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  {user.image ? (
                    <img
                      src={user.image}
                      alt=""
                      className="size-7 rounded-full object-cover"
                    />
                  ) : (
                    <span className="size-7 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                      {initials(user.name)}
                    </span>
                  )}
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                </button>
                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-border bg-background shadow-md py-1 text-sm"
                  >
                    <div className="px-3 py-2 border-b border-border">
                      <p className="font-medium truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </p>
                    </div>
                    <Link
                      to="/dashboard"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-muted"
                    >
                      <LayoutDashboard className="size-4 text-muted-foreground" />
                      Dashboard
                    </Link>
                    <Link
                      to={`/u/${user.id}`}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-muted"
                    >
                      <UserIcon className="size-4 text-muted-foreground" />
                      My profile
                    </Link>
                    <Link
                      to="/submit"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-muted"
                    >
                      <PlusCircle className="size-4 text-muted-foreground" />
                      Submit a spec
                    </Link>
                    {user.isAdmin && (
                      <Link
                        to="/admin/pending"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-muted"
                      >
                        <ShieldCheck className="size-4 text-muted-foreground" />
                        Admin review
                      </Link>
                    )}
                    <Form method="post" action="/auth/logout">
                      <button
                        type="submit"
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-left border-t border-border"
                      >
                        <LogOut className="size-4 text-muted-foreground" />
                        Log out
                      </button>
                    </Form>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                to="/auth/login"
                className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
              >
                <LogIn data-icon="inline-start" />
                Sign in
              </NavLink>
            )}
          </nav>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 rounded-lg hover:bg-muted"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <X className="size-5" />
            ) : (
              <Menu className="size-5" />
            )}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-border py-2 flex flex-col gap-0.5">
            {PRIMARY_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-sm"
              >
                <link.icon className="size-4 text-muted-foreground" />
                {link.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-sm"
                >
                  <LayoutDashboard className="size-4 text-muted-foreground" />
                  Dashboard
                </Link>
                <Link
                  to="/submit"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-sm"
                >
                  <PlusCircle className="size-4 text-muted-foreground" />
                  Submit a spec
                </Link>
                <Link
                  to={`/u/${user.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-sm"
                >
                  <UserIcon className="size-4 text-muted-foreground" />
                  My profile
                </Link>
                {user.isAdmin && (
                  <Link
                    to="/admin/pending"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-sm"
                  >
                    <ShieldCheck className="size-4 text-muted-foreground" />
                    Admin review
                  </Link>
                )}
                <Form method="post" action="/auth/logout">
                  <button
                    type="submit"
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-sm text-left"
                  >
                    <LogOut className="size-4 text-muted-foreground" />
                    Log out
                  </button>
                </Form>
              </>
            ) : (
              <Link
                to="/auth/login"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted text-sm"
              >
                <LogIn className="size-4 text-muted-foreground" />
                Sign in
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
